import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../../config';
import {
  Menu,
  ShoppingCart,
  Users,
  DollarSign,
  Package,
  TrendingUp,
  Truck,
  MessageSquare,
  Settings,
  BarChart3,
  CreditCard,
  FileText,
  LogOut,
  X,
  Image,
  MapPin,
  ChevronDown,
  ChevronRight,
  Shield,
  Activity,
  Key,
  UserPlus,
  Database,
  Bell,
  Mail as MailIcon,
  Phone,
  Globe
} from 'lucide-react';
import NotificationManagement from './NotificationManagement';
import EmailTemplateManagement from './EmailTemplateManagement';
import useInventory from '../../hooks/admin/useInventory';
import InventoryTab from './inventory/InventoryTab';
import { AddProductModal, EditProductModal, ViewProductModal } from './inventory/ProductModals';
import useDelivery from '../../hooks/admin/useDelivery';
import DeliveryTab from './delivery/DeliveryTab';
import DeliveryPersonModal from './delivery/DeliveryPersonModal';
import useOrders from '../../hooks/admin/useOrders';
import OrdersTab from './orders/OrdersTab';
import OrderDetailsModal from './orders/OrderDetailsModal';
import CancelOrderModal from './orders/CancelOrderModal';
import usePayments from '../../hooks/admin/usePayments';
import PaymentsTab from './payments/PaymentsTab';
import useHeroSlides from '../../hooks/admin/useHeroSlides';
import HeroSlidesTab from './heroSlides/HeroSlidesTab';
import { AddHeroSlideModal, EditHeroSlideModal } from './heroSlides/HeroSlideModals';
import useStkTest from '../../hooks/admin/useStkTest';
import StkTestTab from './stkTest/StkTestTab';
import useContactMessages from '../../hooks/admin/useContactMessages';
import ContactMessagesTab from './contact/ContactMessagesTab';
import useSmsManagement from '../../hooks/admin/useSmsManagement';
import SmsManagementTab from './sms/SmsManagementTab';
import SmsTemplateModal from './sms/SmsTemplateModal';
import useSecurity from '../../hooks/admin/useSecurity';
import AdminUsersTab from './security/AdminUsersTab';
import UserAuditsTab from './security/UserAuditsTab';
import ActivityLogsTab from './security/ActivityLogsTab';
import UsersTab from './users/UsersTab';
import useDashboardOverview from '../../hooks/admin/useDashboardOverview';
import DashboardOverviewTab from './dashboard/DashboardOverviewTab';
import usePresence from '../../hooks/admin/usePresence';
import PresenceBar from './PresenceBar';
import NotificationAnalytics from './NotificationAnalytics';
import NotificationSettings from './NotificationSettings';
import OrderStatusWorkflow from './OrderStatusWorkflow';
import RefundManagement from './RefundManagement';
import LocationAnalysis from './LocationAnalysis';
import Advanced360Dashboard from './Advanced360Dashboard';
import SecurityManagement from './SecurityManagement';
import ReportBuilder from './ReportBuilder';
import SystemSettingsEnhanced from './SystemSettingsEnhanced';
import PermissionManager from './PermissionManager';
import NotificationCenter from './NotificationCenter';
import CheckoutCostsManagement from './CheckoutCostsManagement';

const StandaloneAdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [expandedModules, setExpandedModules] = useState({});

  const showNotification = (message, type = 'success') => {
    const notification = document.createElement('div');
    notification.className = `fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 translate-x-full ${
      type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
    }`;
    notification.innerHTML = `
      <div class="flex items-center">
        <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
        </svg>
        <span>${message}</span>
      </div>
    `;
    document.body.appendChild(notification);

    // Animate notification in
    setTimeout(() => {
      notification.classList.remove('translate-x-full');
    }, 100);

    // Auto remove after 3 seconds
    setTimeout(() => {
      notification.classList.add('translate-x-full');
      setTimeout(() => {
        if (document.body.contains(notification)) {
          document.body.removeChild(notification);
        }
      }, 300);
    }, 3000);
  };

  const inventory = useInventory({ activeTab, showNotification });
  const delivery = useDelivery({ activeTab, showNotification });
  const orders = useOrders({ activeTab, showNotification, fetchDeliveryPersons: delivery.fetchDeliveryPersons });
  const payments = usePayments({ activeTab });
  const heroSlides = useHeroSlides({ activeTab, showNotification });
  const stkTest = useStkTest({ showNotification });
  const contact = useContactMessages({ activeTab, showNotification });
  const sms = useSmsManagement({ activeTab });
  const security = useSecurity({ activeTab, showNotification });
  const dashboardOverview = useDashboardOverview({ activeTab, setActiveTab, inventory });

  const [adminData, setAdminData] = useState(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const presence = usePresence({ activeTab, currentAdminId: adminData?.id, showNotification });
  useEffect(() => {
    // Check if admin is logged in
    const token = localStorage.getItem('adminToken');
    const storedAdminData = localStorage.getItem('adminData');
    
    if (!token || !storedAdminData) {
      // Redirect to main site if not logged in — use current frontend origin
      window.location.href = window.location.origin;
      return;
    }

    try {
      setAdminData(JSON.parse(storedAdminData));
      dashboardOverview.fetchDashboardStats();
    } catch (error) {
      console.error('Error parsing admin data:', error);
      window.location.href = window.location.origin;
    }
  }, []);

  const handleLogout = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      await fetch(`${API_BASE_URL}/api/admin/auth/logout`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch (error) {
      console.error('Logout request failed (clearing local session anyway):', error);
    }
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminData');
    window.location.href = window.location.origin;
  };

  // Grouped navigation structure
  const navigationModules = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      type: 'single',
      tabId: 'dashboard'
    },
    {
      id: 'operations',
      label: 'Operations',
      icon: ShoppingCart,
      type: 'group',
      submodules: [
        { id: 'orders', label: 'Orders', icon: ShoppingCart, tabId: 'orders' },
        { id: 'order-workflow', label: 'Order Workflow', icon: TrendingUp, tabId: 'order-workflow' },
        { id: 'delivery', label: 'Delivery', icon: Truck, tabId: 'delivery' },
        { id: 'location-analysis', label: 'Location Analysis', icon: MapPin, tabId: 'location-analysis' }
      ]
    },
    {
      id: 'products',
      label: 'Products',
      icon: Package,
      type: 'group',
      submodules: [
        { id: 'inventory', label: 'Inventory', icon: Package, tabId: 'inventory' },
        { id: 'hero-slides', label: 'Hero Slides', icon: Image, tabId: 'hero-slides' },
        { id: 'checkout-costs', label: 'Checkout Costs', icon: DollarSign, tabId: 'checkout-costs' }
      ]
    },
    {
      id: 'financial',
      label: 'Financial',
      icon: DollarSign,
      type: 'group',
      submodules: [
        { id: 'payments', label: 'Payments', icon: CreditCard, tabId: 'payments' },
        { id: 'refunds', label: 'Refunds', icon: DollarSign, tabId: 'refunds' },
        { id: 'reports', label: 'Advanced Reports', icon: FileText, tabId: 'reports' }
      ]
    },
    {
      id: 'communications',
      label: 'Communications',
      icon: MessageSquare,
      type: 'group',
      submodules: [
        { id: 'notifications', label: 'Notifications', icon: Bell, tabId: 'notifications' },
        { id: 'notifications-center', label: 'Notification Center', icon: Bell, tabId: 'notifications-center' },
        { id: 'email-templates', label: 'Email Templates', icon: MailIcon, tabId: 'email-templates' },
        { id: 'sms', label: 'SMS', icon: Phone, tabId: 'sms' },
        { id: 'contact', label: 'Contact Messages', icon: MessageSquare, tabId: 'contact', badge: contact.newContactCount }
      ]
    },
    {
      id: 'analytics',
      label: 'Analytics',
      icon: BarChart3,
      type: 'group',
      submodules: [
        { id: '360-dashboard', label: '360° Analytics Dashboard', icon: Globe, tabId: '360-dashboard' },
        { id: 'notification-analytics', label: 'Notification Analytics', icon: BarChart3, tabId: 'notification-analytics' }
      ]
    },
    {
      id: 'security',
      label: 'Security',
      icon: Shield,
      type: 'group',
      submodules: [
        { id: 'admin-users', label: 'Admin Users', icon: UserPlus, tabId: 'admin-users' },
        { id: 'user-audits', label: 'User Audits', icon: Activity, tabId: 'user-audits' },
        { id: 'permissions', label: 'Permissions', icon: Key, tabId: 'permissions' },
        { id: 'activity-logs', label: 'Activity Logs', icon: Database, tabId: 'activity-logs' }
      ]
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Settings,
      type: 'group',
      submodules: [
        { id: 'notification-settings', label: 'Notification Settings', icon: Bell, tabId: 'notification-settings' },
        { id: 'system-settings', label: 'System Settings', icon: Settings, tabId: 'system-settings' },
        { id: 'stk-test', label: 'STK Test', icon: CreditCard, tabId: 'stk-test' }
      ]
    },
    {
      id: 'users',
      label: 'Users',
      icon: Users,
      type: 'single',
      tabId: 'users'
    }
  ];

  // Toggle module expansion
  const toggleModule = (moduleId) => {
    setExpandedModules(prev => ({
      ...prev,
      [moduleId]: !prev[moduleId]
    }));
  };

  // Check if a module should be expanded (auto-expand if active tab is in it)
  const isModuleExpanded = (module) => {
    if (expandedModules[module.id] !== undefined) {
      return expandedModules[module.id];
    }
    // Auto-expand if active tab is in this module
    if (module.type === 'group') {
      return module.submodules.some(sub => sub.tabId === activeTab);
    }
    return false;
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardOverviewTab dashboardOverview={dashboardOverview} adminData={adminData} orders={orders} setActiveTab={setActiveTab} />;
      case 'inventory': return <InventoryTab inventory={inventory} dashboardStats={dashboardOverview.dashboardStats} />;
      case 'orders': return <OrdersTab orders={orders} />;
      case 'users': return <UsersTab dashboardStats={dashboardOverview.dashboardStats} />;
      case 'payments': return <PaymentsTab payments={payments} />;
      case 'refunds': return <RefundManagement />;
      case 'hero-slides': return <HeroSlidesTab heroSlides={heroSlides} />;
      case 'checkout-costs': return <CheckoutCostsManagement />;
      case 'sms': return <SmsManagementTab sms={sms} />;
      case 'notifications': return <NotificationManagement />;
      case 'email-templates': return <EmailTemplateManagement />;
      case 'notification-analytics': return <NotificationAnalytics />;
      case 'notification-settings': return <NotificationSettings />;
      case 'order-workflow': return <OrderStatusWorkflow />;
      case 'delivery': return <DeliveryTab delivery={delivery} />;
      case 'location-analysis': return <LocationAnalysis />;
      case 'contact': return <ContactMessagesTab contact={contact} />;
      case 'stk-test': return <StkTestTab stkTest={stkTest} />;
      case 'reports': return <ReportBuilder />;
      case 'system-settings': return <SystemSettingsEnhanced />;
      case '360-dashboard': return <Advanced360Dashboard />;
      case 'notifications-center': return <NotificationCenter />;
      // Security module tabs
      case 'admin-users': return <AdminUsersTab security={security} />;
      case 'user-audits': return <UserAuditsTab security={security} />;
      case 'permissions': return <PermissionManager />;
      case 'activity-logs': return <ActivityLogsTab security={security} />;
      case 'security-management': return <SecurityManagement />;
      default: return <DashboardOverviewTab dashboardOverview={dashboardOverview} adminData={adminData} orders={orders} setActiveTab={setActiveTab} />;
    }
  };

  if (!adminData) {
    return (
      <div className="min-h-screen bg-paper flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto mb-4"></div>
          <p className="text-ink-soft">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-paper overflow-hidden">
      {/* Header */}
      <div className="bg-ink shadow-sm shrink-0">
        <div className="px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <button
                onClick={() => setMobileNavOpen(true)}
                className="lg:hidden p-2 -ml-2 text-paper/80 hover:text-paper hover:bg-paper/10 rounded-full transition-colors shrink-0"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="min-w-0">
                <h1 className="font-display text-xl text-paper truncate">DPC Admin Panel</h1>
                <p className="text-sm text-paper/70 truncate">Welcome back, {adminData.fullName}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <PresenceBar onlineAdmins={presence.onlineAdmins} myOtherSessions={presence.myOtherSessions} recentUpdates={presence.recentUpdates} />
              <button
                onClick={handleLogout}
                className="flex items-center px-3 sm:px-4 py-2.5 text-paper/80 hover:text-paper hover:bg-paper/10 rounded-full transition-colors shrink-0"
              >
                <LogOut className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Mobile nav backdrop */}
        {mobileNavOpen && (
          <div
            className="fixed inset-0 bg-ink/60 backdrop-blur-sm z-40 lg:hidden"
            onClick={() => setMobileNavOpen(false)}
          />
        )}

        {/* Sidebar */}
        <div
          className={`w-64 bg-surface border-r border-line overflow-y-auto fixed inset-y-0 left-0 z-50 transition-transform duration-200 lg:static lg:h-full lg:translate-x-0 ${
            mobileNavOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <div className="flex items-center justify-between px-6 py-4 border-b border-line lg:hidden">
            <span className="font-display text-lg text-ink">Menu</span>
            <button
              onClick={() => setMobileNavOpen(false)}
              className="p-2 text-ink-faint hover:text-ink rounded-full hover:bg-paper transition-colors"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <nav className="mt-6 lg:mt-6">
            {navigationModules.map((module) => {
              const Icon = module.icon;
              const isExpanded = isModuleExpanded(module);

              if (module.type === 'single') {
                // Single item (no submodules)
              return (
                <button
                    key={module.id}
                  onClick={() => {
                      setActiveTab(module.tabId);
                      setMobileNavOpen(false);
                  }}
                  className={`w-full flex items-center justify-between px-6 py-3 text-left transition-colors relative ${
                      activeTab === module.tabId
                      ? 'bg-accent text-paper border-r-4 border-accent-deep'
                      : 'text-ink-soft hover:bg-paper'
                  }`}
                >
                  <div className="flex items-center">
                    <Icon className="h-5 w-5 mr-3" />
                      {module.label}
                  </div>
                  </button>
                );
              } else {
                // Grouped module with submodules
                return (
                  <div key={module.id} className="border-b border-line">
                    <button
                      onClick={() => toggleModule(module.id)}
                      className={`w-full flex items-center justify-between px-6 py-3 text-left transition-colors ${
                        isExpanded
                          ? 'bg-paper text-ink font-semibold'
                          : 'text-ink-soft hover:bg-paper'
                      }`}
                    >
                      <div className="flex items-center">
                        <Icon className="h-5 w-5 mr-3" />
                        {module.label}
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="bg-paper">
                        {module.submodules.map((submodule) => {
                          const SubIcon = submodule.icon;
                          const isActive = activeTab === submodule.tabId;

                          return (
                            <button
                              key={submodule.id}
                              onClick={() => {
                                setActiveTab(submodule.tabId);
                                if (submodule.tabId === 'contact' && submodule.badge > 0) {
                                  contact.setNewContactCount(0);
                                }
                                setMobileNavOpen(false);
                              }}
                              className={`w-full flex items-center justify-between pl-12 pr-6 py-2.5 text-left transition-colors text-sm ${
                                isActive
                                  ? 'bg-accent text-paper border-r-4 border-accent-deep'
                                  : 'text-ink-soft hover:bg-surface'
                              }`}
                            >
                              <div className="flex items-center">
                                <SubIcon className="h-4 w-4 mr-2" />
                                {submodule.label}
                              </div>
                              {submodule.badge > 0 && (
                    <span className="bg-accent-deep text-paper text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                                  {submodule.badge > 9 ? '9+' : submodule.badge}
                    </span>
                  )}
                </button>
              );
                        })}
                      </div>
                    )}
                  </div>
                );
              }
            })}
          </nav>
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {renderContent()}
        </div>
      </div>

      {/* View Product Modal */}
      <ViewProductModal inventory={inventory} />

      {/* Add Product Modal */}
      <AddProductModal inventory={inventory} categories={inventory.categories} />

      {/* Hero Slide Modals */}
      <AddHeroSlideModal heroSlides={heroSlides} />
      <EditHeroSlideModal heroSlides={heroSlides} />

      {/* SMS Template Modal */}
      <SmsTemplateModal sms={sms} />
      
      {/* Edit Product Modal */}
      <EditProductModal inventory={inventory} />

      <OrderDetailsModal orders={orders} delivery={delivery} />
      <CancelOrderModal orders={orders} />
      <DeliveryPersonModal delivery={delivery} />
    </div>
  );
};

export default StandaloneAdminDashboard;
